import type {
  ModelPortfolioType,
  PortfolioReport,
} from '@ovComponents/5-page/portfolioTradingHub/types';
import { TradeSide } from '@ovComponents/5-page/portfolioTradingHub/types';
import { getPortfolioName } from '@ovComponents/5-page/portfolioTradingHub/utils';
import { computeBuyScale } from '@ovComponents/5-page/portfolioTradingHub/utils/reviewRebalanceCalculations';
import type { StagedTrade } from './types';

const MIN_MARKET_VALUE_CENTS = 1;
const UNKNOWN_TICKER = 'UNKNOWN';
const DEFAULT_CURRENCY = 'CAD';

export const getAccountType = (report: PortfolioReport): string => {
  if (report.modelPortfolioType === 'GOAL') return 'Goal';
  const object = report.object;
  if (object && 'account' in object) {
    return object.account?.type || 'Account';
  }
  return 'Account';
};

export const getSubAccountId = (report: PortfolioReport): string =>
  report.subAccounts?.[0]?.subAccount?.id || report.object?.id || '';

export function computeStagedTrades(
  selectedPortfolios: PortfolioReport[],
): StagedTrade[] {
  const trades: StagedTrade[] = [];

  for (const report of selectedPortfolios) {
    const marketValueCents =
      report.object?.statistics?.marketValueCents ||
      report.marketValueCents ||
      MIN_MARKET_VALUE_CENTS;
    const holdings = report.holdings || [];
    const subAccountId = getSubAccountId(report);
    const portfolioName = getPortfolioName(report);
    const accountType = getAccountType(report);
    const modelPortfolioType: ModelPortfolioType =
      report.modelPortfolioType ?? 'SUB_ACCOUNT';
    const objectId = report.object?.id || '';

    const rawDeltas: Array<{
      idx: number;
      holding: (typeof holdings)[0];
      side: TradeSide;
      absDelta: number;
      curPct: number;
    }> = [];
    let totalSellCents = 0;
    let totalBuyCents = 0;

    for (let i = 0; i < holdings.length; i++) {
      const holding = holdings[i];
      const isCash =
        !!holding.financialProduct?.isCash || !!holding.isCashEquivalent;
      if (isCash) continue;

      const targetValueCents = Math.round(
        (holding.expectedPercentage || 0) * marketValueCents,
      );
      const delta = targetValueCents - (holding.valueCents || 0);
      if (delta === 0) continue;

      const side: TradeSide = delta > 0 ? TradeSide.BUY : TradeSide.SELL;
      const absDelta = Math.abs(delta);
      const curPct = ((holding.valueCents || 0) / marketValueCents) * 100;

      if (side === TradeSide.SELL) totalSellCents += absDelta;
      else totalBuyCents += absDelta;

      rawDeltas.push({ idx: i, holding, side, absDelta, curPct });
    }

    const buyScale = computeBuyScale({
      totalBuyCents,
      totalSellCents,
      cashAvailableForTradeCents: report.cashAvailableForTradeCents || 0,
    });

    for (const entry of rawDeltas) {
      const cappedAmount =
        entry.side === TradeSide.BUY
          ? Math.round(entry.absDelta * buyScale)
          : entry.absDelta;
      if (cappedAmount === 0) continue;

      trades.push({
        id: `${report.id}-${entry.idx}`,
        portfolioReportId: report.id,
        portfolioName,
        accountType,
        subAccountId,
        financialProductId: entry.holding.financialProduct?.id || '',
        ticker: entry.holding.financialProduct?.ticker || UNKNOWN_TICKER,
        productName:
          entry.holding.financialProduct?.translatedName?.en ||
          entry.holding.financialProduct?.name ||
          entry.holding.financialProduct?.ticker ||
          '',
        currency:
          entry.holding.currency ||
          entry.holding.financialProduct?.currency ||
          DEFAULT_CURRENCY,
        side: entry.side,
        amountCents: cappedAmount,
        currentValueCents: entry.holding.valueCents || 0,
        expectedValueCents: entry.holding.expectedValueCents || 0,
        currentPct: entry.curPct,
        marketValueCents,
        removed: false,
        modelPortfolioType,
        objectId,
      });
    }
  }

  return trades;
}
